import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Shield, Users, Search, Eye, Edit3, KeyRound, LogOut, Trash2,
  ScrollText, Sword, BookOpenCheck, Activity, Crown, Sparkles, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageLoader, PageHeader } from "@/components/common";

const ROLES = ["player", "dm", "admin"];
const DEFAULT_ADMIN_EMAIL = "misterminecrafter360@gmail.com";

const roleStyles = {
  admin: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  dm: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  player: "bg-zinc-500/15 text-zinc-300 border-zinc-500/25",
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dialog, setDialog] = useState(null); // {kind, user}

  // Hard gate — bounces non-admins (runs after all hooks to comply with rules-of-hooks).
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    onError: (e) => {
      if (e?.response?.status === 403) navigate("/", { replace: true });
    },
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
    onError: (e) => {
      if (e?.response?.status === 403) navigate("/", { replace: true });
    },
  });

  const filtered = useMemo(() => {
    let list = users || [];
    if (roleFilter) list = list.filter((u) => u.role === roleFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((u) =>
        u.email.toLowerCase().includes(s) || (u.username || "").toLowerCase().includes(s),
      );
    }
    return list;
  }, [users, search, roleFilter]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  return (
    <div>
      <PageHeader title="Admin Console" subtitle="User & system management" icon={Shield} />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatTile icon={Users} label="Users" value={stats?.total_users} color="violet" />
        <StatTile icon={Crown} label="Admins" value={stats?.roles?.admins} color="violet" />
        <StatTile icon={Shield} label="DMs" value={stats?.roles?.dms} color="amber" />
        <StatTile icon={Sparkles} label="Players" value={stats?.roles?.players} color="teal" />
        <StatTile icon={ScrollText} label="Campaigns" value={stats?.total_campaigns} color="rose" />
        <StatTile icon={Sword} label="Characters" value={stats?.total_characters} color="blue" />
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="input-dark w-full rounded-xl pl-11 pr-4 py-2.5 text-sm"
          />
        </div>
        <div className="flex gap-1.5">
          {["", ...ROLES].map((r) => (
            <button
              key={r || "all"}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-2.5 rounded-xl text-xs font-medium capitalize whitespace-nowrap transition ${
                roleFilter === r ? "btn-violet" : "glass glass-hover text-zinc-400"
              }`}
            >
              {r || "all"}
            </button>
          ))}
        </div>
      </div>

      {/* User list */}
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-white/[0.06]">
            <div className="col-span-3">User</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-1">Role</div>
            <div className="col-span-2">Campaigns</div>
            <div className="col-span-1">Sessions</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {filtered.map((u) => (
            <UserRow
              key={u.user_id}
              row={u}
              me={user}
              onAction={(kind) => setDialog({ kind, user: u })}
            />
          ))}
          {filtered.length === 0 && (
            <p className="text-zinc-600 text-center py-10 text-sm">No users match.</p>
          )}
        </div>
      )}

      {/* Dialogs */}
      {dialog?.kind === "view" && <ViewDialog user={dialog.user} onClose={() => setDialog(null)} />}
      {dialog?.kind === "edit" && (
        <EditDialog user={dialog.user} onClose={() => setDialog(null)} onSuccess={refresh} />
      )}
      {dialog?.kind === "reset" && (
        <ResetDialog user={dialog.user} onClose={() => setDialog(null)} onSuccess={refresh} />
      )}
      {dialog?.kind === "logout" && (
        <ForceLogoutDialog user={dialog.user} onClose={() => setDialog(null)} onSuccess={refresh} />
      )}
      {dialog?.kind === "delete" && (
        <DeleteDialog user={dialog.user} onClose={() => setDialog(null)} onSuccess={refresh} />
      )}
    </div>
  );
}

// ============================ Subcomponents ============================

function StatTile({ icon: Icon, label, value, color }) {
  const colors = {
    violet: "text-violet-300 bg-violet-500/15",
    amber: "text-amber-300 bg-amber-500/15",
    teal: "text-teal-300 bg-teal-500/15",
    rose: "text-rose-300 bg-rose-500/15",
    blue: "text-blue-300 bg-blue-500/15",
  };
  return (
    <div className="glass rounded-xl p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colors[color] || colors.violet}`}>
          <Icon size={14} />
        </div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      </div>
      <div className="text-2xl font-bold text-white font-display">{value ?? "–"}</div>
    </div>
  );
}

function UserRow({ row, me, onAction }) {
  const isDefaultAdmin = row.email?.toLowerCase() === DEFAULT_ADMIN_EMAIL;
  const isSelf = row.user_id === (me?.id || me?._id || me?.user_id);
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] transition items-center">
      {/* user */}
      <div className="md:col-span-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
          {(row.username || row.email || "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-white text-sm flex items-center gap-1.5">
            {row.username || "—"}
            {isDefaultAdmin && <Crown size={12} className="text-amber-400" title="Default admin" />}
          </div>
          <div className="text-[10px] text-zinc-500 md:hidden truncate">{row.email}</div>
        </div>
      </div>
      <div className="md:col-span-3 text-xs text-zinc-400 truncate hidden md:block">{row.email}</div>
      <div className="md:col-span-1">
        <span className={`text-[10px] px-2 py-1 rounded-full border font-medium uppercase tracking-wider ${roleStyles[row.role] || roleStyles.player}`}>
          {row.role}
        </span>
      </div>
      <div className="md:col-span-2 text-xs text-zinc-400">
        <span className="text-amber-300">{row.campaigns_as_dm}</span> DM ·{" "}
        <span className="text-teal-300">{row.campaigns_as_player}</span> P
      </div>
      <div className="md:col-span-1 text-xs text-zinc-400">
        <Activity size={11} className="inline mr-1" />
        {row.active_sessions || 0}
      </div>
      <div className="md:col-span-2 flex gap-1 justify-end">
        <IconBtn icon={Eye} title="View details" onClick={() => onAction("view")} />
        <IconBtn icon={Edit3} title="Edit user" onClick={() => onAction("edit")} />
        <IconBtn icon={KeyRound} title="Reset password" onClick={() => onAction("reset")} />
        <IconBtn icon={LogOut} title="Force logout" onClick={() => onAction("logout")} />
        <IconBtn
          icon={Trash2}
          title={isDefaultAdmin ? "Cannot delete default admin" : isSelf ? "Cannot delete yourself" : "Delete user"}
          danger
          disabled={isDefaultAdmin || isSelf}
          onClick={() => onAction("delete")}
        />
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, title, onClick, danger, disabled }) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
        disabled
          ? "opacity-30 cursor-not-allowed"
          : danger
            ? "hover:bg-rose-500/20 text-zinc-400 hover:text-rose-300"
            : "hover:bg-violet-500/15 text-zinc-400 hover:text-violet-300"
      }`}
    >
      <Icon size={14} />
    </button>
  );
}

// ============================ Dialogs ============================

function Dialog({ title, children, onClose, footer }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="glass rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <h3 className="font-display text-lg text-white">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="p-4 border-t border-white/[0.06] flex gap-2 justify-end">{footer}</div>}
      </div>
    </div>
  );
}

function ViewDialog({ user, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-user-detail", user.user_id],
    queryFn: async () => (await api.get(`/admin/users/${user.user_id}`)).data,
  });
  return (
    <Dialog title={`Details: ${user.username}`} onClose={onClose}>
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="space-y-3 text-sm">
          <Row label="Email" value={data?.user?.email} />
          <Row label="Username" value={data?.user?.username} />
          <Row label="Role" value={data?.user?.role} />
          <Row label="Created" value={data?.user?.created_at} />
          <Row label="External ID" value={data?.user?.external_id} />
          <Row label="Characters" value={data?.characters?.length ?? 0} />
          <Row label="DM in" value={data?.campaigns_as_dm?.length ?? 0} />
          <Row label="Player in" value={data?.campaigns_as_player?.length ?? 0} />
          {data?.campaigns_as_dm?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">DM Campaigns</div>
              <ul className="text-xs text-zinc-300 space-y-0.5">
                {data.campaigns_as_dm.map((c) => <li key={c.campaign_id}>• {c.name}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-zinc-500 text-xs uppercase tracking-wider">{label}</span>
      <span className="text-zinc-200 text-right break-all">{value ?? "—"}</span>
    </div>
  );
}

function EditDialog({ user, onClose, onSuccess }) {
  const [username, setUsername] = useState(user.username || "");
  const [role, setRole] = useState(user.role || "player");
  const isDefaultAdmin = user.email?.toLowerCase() === DEFAULT_ADMIN_EMAIL;

  const m = useMutation({
    mutationFn: async () => {
      const body = {};
      if (username !== user.username) body.username = username;
      if (role !== user.role) body.role = role;
      return (await api.put(`/admin/users/${user.user_id}/update`, body)).data;
    },
    onSuccess: () => { toast.success("User updated"); onSuccess(); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });

  return (
    <Dialog
      title="Edit user"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:bg-white/5">Cancel</button>
          <button onClick={() => m.mutate()} disabled={m.isPending} className="btn-violet px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            {m.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wider">Username</label>
          <input value={username} onChange={(e) => setUsername(e.target.value)} className="input-dark w-full rounded-lg px-3 py-2 mt-1 text-sm" />
        </div>
        <div>
          <label className="text-xs text-zinc-500 uppercase tracking-wider">Role</label>
          <select
            value={role}
            disabled={isDefaultAdmin}
            onChange={(e) => setRole(e.target.value)}
            className="input-dark w-full rounded-lg px-3 py-2 mt-1 text-sm disabled:opacity-50"
          >
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {isDefaultAdmin && (
            <p className="text-[11px] text-amber-300/80 mt-1 flex items-center gap-1">
              <Crown size={10} /> Default admin role is locked.
            </p>
          )}
        </div>
      </div>
    </Dialog>
  );
}

function ResetDialog({ user, onClose, onSuccess }) {
  const [pw, setPw] = useState("");
  const m = useMutation({
    mutationFn: async () => (await api.put(`/admin/users/${user.user_id}/reset-password`, { password: pw })).data,
    onSuccess: () => { toast.success("Password reset — user force-logged-out"); onSuccess(); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Reset failed"),
  });
  return (
    <Dialog
      title="Reset password"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:bg-white/5">Cancel</button>
          <button onClick={() => m.mutate()} disabled={pw.length < 8 || m.isPending} className="btn-violet px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            {m.isPending ? "Resetting…" : "Reset password"}
          </button>
        </>
      }
    >
      <p className="text-xs text-zinc-400 mb-3">
        Setting a new password for <span className="text-white">{user.email}</span>. They will be
        immediately logged out of every active session and must use the new password to sign back in.
      </p>
      <input
        type="password"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        placeholder="New password (min 8 chars)"
        className="input-dark w-full rounded-lg px-3 py-2 text-sm"
      />
    </Dialog>
  );
}

function ForceLogoutDialog({ user, onClose, onSuccess }) {
  const m = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${user.user_id}/force-logout`)).data,
    onSuccess: () => { toast.success("User force-logged-out"); onSuccess(); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Logout failed"),
  });
  return (
    <Dialog
      title="Force logout"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:bg-white/5">Cancel</button>
          <button onClick={() => m.mutate()} disabled={m.isPending} className="btn-violet px-4 py-2 rounded-lg text-sm disabled:opacity-50">
            {m.isPending ? "Working…" : "Force logout"}
          </button>
        </>
      }
    >
      <p className="text-sm text-zinc-400">
        Invalidate every active session for <span className="text-white">{user.email}</span>?
        They will have to log in again on every device.
      </p>
    </Dialog>
  );
}

function DeleteDialog({ user, onClose, onSuccess }) {
  const [confirm, setConfirm] = useState("");
  const canConfirm = confirm === user.email;
  const m = useMutation({
    mutationFn: async () => (await api.delete(`/admin/users/${user.user_id}`)).data,
    onSuccess: () => { toast.success("User deleted"); onSuccess(); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });
  return (
    <Dialog
      title="Delete user"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:bg-white/5">Cancel</button>
          <button
            onClick={() => m.mutate()}
            disabled={!canConfirm || m.isPending}
            className="px-4 py-2 rounded-lg text-sm bg-rose-500/20 text-rose-300 border border-rose-500/30 hover:bg-rose-500/30 disabled:opacity-30"
          >
            {m.isPending ? "Deleting…" : "Delete permanently"}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-2 mb-3 p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
        <AlertTriangle size={16} className="text-rose-400 shrink-0 mt-0.5" />
        <p className="text-xs text-rose-200">
          This will permanently delete the user and cascade-delete their characters,
          DM-owned campaigns, and dice rolls. This cannot be undone.
        </p>
      </div>
      <p className="text-xs text-zinc-400 mb-2">Type <span className="text-white font-mono">{user.email}</span> to confirm:</p>
      <input
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder={user.email}
        className="input-dark w-full rounded-lg px-3 py-2 text-sm font-mono"
      />
    </Dialog>
  );
}
